import React, { useState } from 'react';
import { useUpdateSite, useDeleteSite, useManageSiteDatabase } from '../helpers/useCloudflareApi';
import { Button } from './Button';
import { Input } from './Input';
import { Badge } from './Badge';
import { Spinner } from './Spinner';
import {
  ExternalLink,
  Copy,
  Trash2,
  Edit2,
  Check,
  X,
  MoreHorizontal,
  Database,
  Shield,
} from 'lucide-react';
import { SiteSecretsDialog } from './SiteSecretsDialog';
import { toast } from 'sonner';
import { formatDateTimeShort } from '../helpers/formatDateUTC';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from './DropdownMenu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './Dialog';
import styles from './PublishedSiteItem.module.css';
import { Selectable } from 'kysely';
import { PublishedSites } from '../helpers/schema';

interface PublishedSiteItemProps {
  site: Selectable<PublishedSites>;
}

export const PublishedSiteItem: React.FC<PublishedSiteItemProps> = ({ site }) => {
  const updateSiteMutation = useUpdateSite();
  const deleteSiteMutation = useDeleteSite();
  const manageDatabaseMutation = useManageSiteDatabase();

  const [isEditing, setIsEditing] = useState(false);
  const [editSubdomain, setEditSubdomain] = useState(site.subdomain);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [databaseResetConfirmOpen, setDatabaseResetConfirmOpen] = useState(false);
  const [secretsDialogOpen, setSecretsDialogOpen] = useState(false);

  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    toast.success('URL copied to clipboard');
  };

  const handleStartEdit = () => {
    setIsEditing(true);
    setEditSubdomain(site.subdomain);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditSubdomain(site.subdomain);
  };

  const handleSaveEdit = async () => {
    if (!editSubdomain || editSubdomain.length < 3) {
      toast.error('Subdomain must be at least 3 characters');
      return;
    }

    try {
      await updateSiteMutation.mutateAsync({
        siteId: site.id,
        subdomain: editSubdomain,
      });
      toast.success('Site updated successfully');
      setIsEditing(false);
    } catch (err) {
      toast.error('Failed to update site');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteSiteMutation.mutateAsync({ siteId: site.id });
      toast.success('Site deleted successfully');
      setDeleteConfirmOpen(false);
    } catch (err) {
      toast.error('Failed to delete site');
    }
  };

  const handleDatabaseAction = async (action: 'enable' | 'disable' | 'reset') => {
    try {
      if (action === 'reset') {
        // Close menu handled by DropdownMenu usually, but we need to open dialog
        setDatabaseResetConfirmOpen(true);
        return;
      }

      const actionText = action === 'enable' ? 'enabling' : 'disabling';
      toast.promise(manageDatabaseMutation.mutateAsync({ siteId: site.id, action }), {
        loading: `${action === 'enable' ? 'Enabling' : 'Disabling'} database...`,
        success: `Database ${action}d successfully`,
        error: `Failed to ${action} database`,
      });
    } catch (err) {
      // Error handled by toast promise
    }
  };

  const handleResetDatabase = async () => {
    try {
      await manageDatabaseMutation.mutateAsync({ siteId: site.id, action: 'reset' });
      toast.success('Database reset successfully');
      setDatabaseResetConfirmOpen(false);
    } catch (err) {
      toast.error('Failed to reset database');
    }
  };

  return (
    <div className={styles.siteCard}>
      <div className={styles.siteHeader}>
        <div className={styles.siteInfo}>
          {isEditing ? (
            <div className={styles.editRow}>
              <Input
                value={editSubdomain}
                onChange={(e) => setEditSubdomain(e.target.value)}
                className={styles.editInput}
                autoFocus
              />
              <span className={styles.domainSuffix}>.tasking.tech</span>
              <div className={styles.editActions}>
                <Button
                  size="icon-sm"
                  variant="ghost"
                  onClick={handleSaveEdit}
                  disabled={updateSiteMutation.isPending}
                >
                  {updateSiteMutation.isPending ? <Spinner size="sm" /> : <Check size={14} />}
                </Button>
                <Button
                  size="icon-sm"
                  variant="ghost"
                  onClick={handleCancelEdit}
                  disabled={updateSiteMutation.isPending}
                >
                  <X size={14} />
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className={styles.urlRow}>
                <a
                  href={
                    site.taskingTechSubdomain
                      ? `https://${site.taskingTechSubdomain}`
                      : site.deploymentUrl || '#'
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.siteLink}
                  onClick={(e) => {
                    e.preventDefault();
                    window.open(
                      site.taskingTechSubdomain
                        ? `https://${site.taskingTechSubdomain}`
                        : site.deploymentUrl || '#',
                      '_blank',
                      'noopener,noreferrer',
                    );
                  }}
                >
                  {site.taskingTechSubdomain || `${site.subdomain}.tasking.tech`}
                  <ExternalLink size={12} />
                </a>
                {site.hasDatabase && (
                  <div className={styles.dbIndicator} title="Database Enabled">
                    <Database size={12} />
                    <span>D1</span>
                  </div>
                )}
              </div>
              <div className={styles.visitButtonRow}>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    window.open(
                      site.taskingTechSubdomain
                        ? `https://${site.taskingTechSubdomain}`
                        : site.deploymentUrl || '#',
                      '_blank',
                    )
                  }
                  className={styles.visitButton}
                >
                  <ExternalLink size={14} />
                  Visit Site
                </Button>
              </div>
            </>
          )}
          <div className={styles.metaRow}>
            <Badge
              variant={site.deploymentStatus === 'deployed' ? 'success' : 'secondary'}
              className={styles.statusBadge}
            >
              {site.deploymentStatus}
            </Badge>
            <span className={styles.date}>
              {site.lastDeployedAt ? formatDateTimeShort(site.lastDeployedAt) : 'Just now'}
            </span>
          </div>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon-sm" className={styles.menuButton}>
              <MoreHorizontal size={16} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Site Actions</DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() =>
                window.open(
                  site.taskingTechSubdomain
                    ? `https://${site.taskingTechSubdomain}`
                    : site.deploymentUrl || '',
                  '_blank',
                )
              }
            >
              <ExternalLink size={14} className={styles.menuIcon} />
              Open Site
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() =>
                handleCopyUrl(
                  site.taskingTechSubdomain
                    ? `https://${site.taskingTechSubdomain}`
                    : site.deploymentUrl || '',
                )
              }
            >
              <Copy size={14} className={styles.menuIcon} />
              Copy URL
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleStartEdit}>
              <Edit2 size={14} className={styles.menuIcon} />
              Edit Subdomain
            </DropdownMenuItem>

            <DropdownMenuSeparator />
            <DropdownMenuLabel>Database</DropdownMenuLabel>
            {site.hasDatabase ? (
              <>
                <DropdownMenuItem onClick={() => handleDatabaseAction('reset')}>
                  <Database size={14} className={styles.menuIcon} />
                  Reset Database
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleDatabaseAction('disable')}
                  disabled={manageDatabaseMutation.isPending}
                >
                  <Database size={14} className={styles.menuIcon} />
                  Disable Database
                </DropdownMenuItem>
              </>
            ) : (
              <DropdownMenuItem
                onClick={() => handleDatabaseAction('enable')}
                disabled={manageDatabaseMutation.isPending}
              >
                <Database size={14} className={styles.menuIcon} />
                Enable Database
              </DropdownMenuItem>
            )}

            <DropdownMenuSeparator />
            <DropdownMenuLabel>Secrets</DropdownMenuLabel>
            <DropdownMenuItem onClick={() => setSecretsDialogOpen(true)}>
              <Shield size={14} className={styles.menuIcon} />
              Manage Secrets
            </DropdownMenuItem>

            <DropdownMenuSeparator />
            <DropdownMenuItem
              className={styles.deleteItem}
              onClick={() => setDeleteConfirmOpen(true)}
            >
              <Trash2 size={14} className={styles.menuIcon} />
              Delete Site
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Site</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this site? This action cannot be undone and the
              subdomain will be released.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteSiteMutation.isPending}
            >
              {deleteSiteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Secrets Management Dialog */}
      <Dialog open={secretsDialogOpen} onOpenChange={setSecretsDialogOpen}>
        <SiteSecretsDialog site={site} onClose={() => setSecretsDialogOpen(false)} />
      </Dialog>

      {/* Database Reset Confirmation Dialog */}
      <Dialog open={databaseResetConfirmOpen} onOpenChange={setDatabaseResetConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Database</DialogTitle>
            <DialogDescription>
              Are you sure you want to reset the database? This will delete all data in the database
              and re-apply the schema. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDatabaseResetConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleResetDatabase}
              disabled={manageDatabaseMutation.isPending}
            >
              {manageDatabaseMutation.isPending ? 'Resetting...' : 'Reset Database'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
